import { MarketType, ProductPermission, SpreadsheetMapping, TradingEntityProductPermission, ValidationIssue } from 'api'
import { Locale, Translation } from 'locale'
import { gatherIssues, parseEnum, parseString, SpreadsheetParseResult } from '../upload/model'

export interface TradingEntityProductAttributes {
  orgShortCode: string | null
  shortName: string | null
  productName: string | null
  sectorName: string | null
  permission: ProductPermission | null
  marketType: MarketType
}

interface TradingEntityProductSpreadsheetParseRowResult {
  tradingEntityProduct: TradingEntityProductAttributes
  parseIssues: ValidationIssue[]
}

export interface TradingEntityProductSpreadsheetParseResult extends SpreadsheetParseResult {
  tradingEntityProducts: TradingEntityProductAttributes[]
}

export function parseTradingEntityProductSpreadsheet(
  filename: string,
  rows: any[],
  spreadsheetMapping: SpreadsheetMapping
): TradingEntityProductSpreadsheetParseResult {
  return rows.reduce<TradingEntityProductSpreadsheetParseResult>(
    (spreadsheetResult, row, rowIdx) => {
      const rowResult = parseRow(row, rowIdx + 1, spreadsheetMapping)

      spreadsheetResult.issues.push(...rowResult.parseIssues)

      if (rowResult.tradingEntityProduct && rowResult.parseIssues.length === 0) {
        spreadsheetResult.tradingEntityProducts.push(rowResult.tradingEntityProduct)
      }

      return spreadsheetResult
    },
    { filename, rows, issues: [], tradingEntityProducts: [] }
  )
}

function parseRow(row: any, rowNum: number, spreadsheetMapping: SpreadsheetMapping): TradingEntityProductSpreadsheetParseRowResult {
  const organisationResult = parseString(
    row,
    rowNum,
    spreadsheetMapping.organisation,
    Locale.translate(Translation.TC_ORGANISATION_SHORT_CODE),
    true
  )
  const shortNameResult = parseString(
    row,
    rowNum,
    spreadsheetMapping.shortName,
    Locale.translate(Translation.TC_TRADING_ENTITY_SHORT_NAME),
    true
  )
  const productResult = parseString(row, rowNum, spreadsheetMapping.product, Locale.translate(Translation.TC_PRODUCT), true)
  const sectorResult = parseString(row, rowNum, spreadsheetMapping.sector, Locale.translate(Translation.TC_SECTOR), false)
  const permissionResult = parseEnum<TradingEntityProductPermission>(
    row,
    rowNum,
    spreadsheetMapping.permission,
    spreadsheetMapping.permission.values,
    Locale.translate(Translation.TC_PERMISSION),
    false,
    true
  )

  const parseIssues = gatherIssues(organisationResult, shortNameResult, productResult, sectorResult, permissionResult)

  return {
    parseIssues,
    tradingEntityProduct: {
      orgShortCode: organisationResult.isValue ? organisationResult.value : null,
      shortName: shortNameResult.isValue ? shortNameResult.value : null,
      productName: productResult.isValue ? productResult.value : null,
      sectorName: sectorResult.isValue ? sectorResult.value : null,
      permission: permissionResult.isValue ? ProductPermissionLookup[permissionResult.value] ?? null : null,
      // [HK]: To check, we might need to get the actual market type for that product sector
      marketType: MarketType.PRIMARY
    }
  }
}

const ProductPermissionLookup: Record<TradingEntityProductPermission, ProductPermission> = {
  [TradingEntityProductPermission.VIEW]: ProductPermission.VIEW,
  [TradingEntityProductPermission.TRADE]: ProductPermission.TRADE
}
